/**
 * Lowers the export scene's SceneWalls to planar boundary faces: every leaf
 * of a wall its own closed body, the faces of its square-ended slab.
 *
 * A leaf's body: whole-leaf inner and outer surfaces as regions (the leaf
 * rectangle minus its cutout rectangles: a window is a real hole loop in the
 * face, a door on the floor a notch from the bottom edge, a floor-to-ceiling
 * opening a split), cutout reveals (a sill top, lintel bottom, or jamb only
 * where the cutout stops short of that edge), the two square ends minus the
 * bands of cutouts running out through them, and footprint caps slotted
 * where cutouts reach the ceiling or the floor.
 */

import { type Point, subtractPolygons } from '@kaiizen/kai-floor-plan';
import { type SceneWall, type SceneWallCutout, type SceneWallLeaf } from './mesh3d';
import { face, FACE_EPSILON, mirror, type PlanarFace, type SpacePoint } from './planarFace';

/** An axis-aligned rectangle ring in face-local coordinates. */
const rectRing = (x0: number, y0: number, x1: number, y1: number): Point[] => [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }];

/** The leaf-local rectangle [0, length] × [bottom, top] minus the cutouts within it, as polygons with holes / notches. */
const rectMinusCutouts = (length: number, bottom: number, top: number, cutouts: SceneWallCutout[]): Point[][][] => {
  const inside = cutouts.filter(cutout => cutout.s1 - cutout.s0 > FACE_EPSILON && cutout.top - cutout.base > FACE_EPSILON);
  return subtractPolygons(
    [rectRing(0, bottom, length, top)],
    inside.map(cutout => [rectRing(cutout.s0, Math.max(bottom, cutout.base), cutout.s1, Math.min(top, cutout.top))]),
  );
};

interface LeafFrame {
  length: number;
  along: Point;
  /** The outward unit normal (toward the outer face). */
  normal: Point;
  /** The slab's along-range: the inner line run out through both square ends. */
  slabStart: number;
  slabEnd: number;
  cutouts: SceneWallCutout[];
  /** A point of the slab: `s` along the inner line, `side` thicknesses across (0 inner, 1 outer), at height `h`. */
  at: (s: number, side: number, h: number) => SpacePoint;
}

/**
 * The leaf's slab frame in the mirrored export plane. The leaf's own length
 * is the cutouts' frame; its points only place it. The outward unit normal
 * points toward the mitered outer corner, and the wall's thickness is
 * measured along it: the perpendicular distance between the lines. The
 * slab's corners are computed once, so every face welds bit-for-bit at them.
 */
const leafFrame = (leaf: SceneWallLeaf): LeafFrame => {
  const innerStart = mirror(leaf.innerStart), innerEnd = mirror(leaf.innerEnd);
  const outerStart = mirror(leaf.outerStart);
  const { length } = leaf;
  const measured = Math.hypot(innerEnd.x - innerStart.x, innerEnd.y - innerStart.y);
  const along = { x: (innerEnd.x - innerStart.x) / measured, y: (innerEnd.y - innerStart.y) / measured };
  const toOuter = { x: outerStart.x - innerStart.x, y: outerStart.y - innerStart.y };
  const normal = along.y * toOuter.x - along.x * toOuter.y >= 0 ? { x: along.y, y: -along.x } : { x: -along.y, y: along.x };
  const thickness = Math.abs(normal.x * toOuter.x + normal.y * toOuter.y);
  const slabStart = -leaf.startExtension, slabEnd = length + leaf.endExtension;
  const point = (s: number, side: number): Point => ({
    x: innerStart.x + along.x * s + normal.x * thickness * side,
    y: innerStart.y + along.y * s + normal.y * thickness * side,
  });
  const corners = { innerStart: point(slabStart, 0), outerStart: point(slabStart, 1), innerEnd: point(slabEnd, 0), outerEnd: point(slabEnd, 1) };
  const at = (s: number, side: number, h: number): SpacePoint => {
    const corner = s <= slabStart && side <= 0
      ? corners.innerStart
      : s <= slabStart && side >= 1
      ? corners.outerStart
      : s >= slabEnd && side <= 0
      ? corners.innerEnd
      : s >= slabEnd && side >= 1
      ? corners.outerEnd
      : point(Math.min(slabEnd, Math.max(slabStart, s)), side);
    return { x: corner.x, y: corner.y, h };
  };
  return { length, along, normal, slabStart, slabEnd, cutouts: leaf.cutouts, at };
};

/** The along-spans of [0, length] not covered by the (sorted, disjoint) spans given. */
const runsBetween = (length: number, covered: readonly [number, number][]): [number, number][] => {
  const runs: [number, number][] = [];
  let cursor = 0;
  for (const [s0, s1] of covered) {
    if (s0 > cursor + FACE_EPSILON) runs.push([cursor, s0]);
    cursor = Math.max(cursor, s1);
  }
  if (cursor < length - FACE_EPSILON) runs.push([cursor, length]);
  return runs;
};

/**
 * The boundary faces of one leaf's slab: inner and outer surfaces with the
 * cutouts as holes or notches, the reveals lining each cutout where it stops
 * short of an edge, the two square ends minus the bands of cutouts reaching
 * them, and a footprint cap per run of solid material between the cutouts
 * that reach that cap. Every vertex comes through the frame's `at`. In slab
 * coordinates a cutout reaching an end of the leaf runs out through the
 * square end with it.
 */
const leafFaces = (leaf: LeafFrame, bottom: number, top: number): PlanarFace[] => {
  const { slabStart, slabEnd, along, normal } = leaf;
  const slabLength = slabEnd - slabStart;
  const cutouts = leaf.cutouts.map(cutout => ({
    ...cutout,
    s0: (cutout.s0 <= FACE_EPSILON ? slabStart : cutout.s0) - slabStart,
    s1: (cutout.s1 >= leaf.length - FACE_EPSILON ? slabEnd : cutout.s1) - slabStart,
  }));
  const at = (s: number, side: number, h: number) => leaf.at(slabStart + s, side, h);
  const faces: PlanarFace[] = [];

  for (const polygon of rectMinusCutouts(slabLength, bottom, top, cutouts)) {
    faces.push(face(polygon, facePoint => at(facePoint.x, 0, facePoint.y), { x: -normal.x, y: -normal.y, h: 0 }));
    faces.push(face(polygon, facePoint => at(facePoint.x, 1, facePoint.y), { x: normal.x, y: normal.y, h: 0 }));
  }

  for (const cutout of cutouts) {
    const acrossRect: Point[][] = [[{ x: 0, y: cutout.base }, { x: 1, y: cutout.base }, { x: 1, y: cutout.top }, { x: 0, y: cutout.top }]];
    const spanRect: Point[][] = [[{ x: cutout.s0, y: 0 }, { x: cutout.s1, y: 0 }, { x: cutout.s1, y: 1 }, { x: cutout.s0, y: 1 }]];
    if (cutout.base > bottom + FACE_EPSILON) faces.push(face(spanRect, facePoint => at(facePoint.x, facePoint.y, cutout.base), { x: 0, y: 0, h: 1 }));
    if (cutout.top < top - FACE_EPSILON) faces.push(face(spanRect, facePoint => at(facePoint.x, facePoint.y, cutout.top), { x: 0, y: 0, h: -1 }));
    if (cutout.s0 > FACE_EPSILON) faces.push(face(acrossRect, facePoint => at(cutout.s0, facePoint.x, facePoint.y), { x: along.x, y: along.y, h: 0 }));
    if (cutout.s1 < slabLength - FACE_EPSILON) {
      faces.push(face(acrossRect, facePoint => at(cutout.s1, facePoint.x, facePoint.y), { x: -along.x, y: -along.y, h: 0 }));
    }
  }

  const bands = (touching: SceneWallCutout[]) => touching.map(cutout => ({ s0: 0, s1: 1, base: cutout.base, top: cutout.top }));
  for (const polygon of rectMinusCutouts(1, bottom, top, bands(cutouts.filter(cutout => cutout.s0 <= FACE_EPSILON)))) {
    faces.push(face(polygon, facePoint => at(0, facePoint.x, facePoint.y), { x: -along.x, y: -along.y, h: 0 }));
  }
  for (const polygon of rectMinusCutouts(1, bottom, top, bands(cutouts.filter(cutout => cutout.s1 >= slabLength - FACE_EPSILON)))) {
    faces.push(face(polygon, facePoint => at(slabLength, facePoint.x, facePoint.y), { x: along.x, y: along.y, h: 0 }));
  }

  const cap = (reaches: (cutout: SceneWallCutout) => boolean, h: number, up: number) => {
    for (const [runStart, runEnd] of runsBetween(slabLength, cutouts.filter(reaches).map(cutout => [cutout.s0, cutout.s1]))) {
      const ring = [at(runStart, 0, h), at(runEnd, 0, h), at(runEnd, 1, h), at(runStart, 1, h)].map(({ x, y }) => ({ x, y }));
      faces.push(face([ring], point => ({ x: point.x, y: point.y, h }), { x: 0, y: 0, h: up }));
    }
  };
  cap(cutout => cutout.top >= top - FACE_EPSILON, top, 1);
  cap(cutout => cutout.base <= bottom + FACE_EPSILON, bottom, -1);
  return faces;
};

/** Every leaf of the wall as its own closed body: the faces of its square-ended slab. */
export const wallBodies = (wall: SceneWall): PlanarFace[][] => wall.leaves.map(leaf => leafFaces(leafFrame(leaf), wall.base, wall.top));
